/**
 * motion.ts - jw-pds 의 모션 레이어 (순수 TS, DOM 의존 최소)
 *
 * 왜 애니메이션 라이브러리를 넣지 않았나
 *   MOTION_INTENSITY 4 에서 모션이 하는 일은 두 가지뿐이다.
 *   (1) 피드백 - 눌렸다는 것을 즉시 알리기
 *   (2) 상태 전이 - 무엇이 어디서 왔는지 보이기
 *   (1)(2) 는 CSS 트랜지션으로 끝난다. 라이브러리가 필요한 것은
 *   제스처 구동 모션(1:1 추적 -> 속도 인계 -> 스프링 안착)인데,
 *   그건 아래 60줄로 충분하다. Motion 을 통째로 넣는 것보다
 *   이게 작고, 프레임을 남과 다투지 않는다.
 *
 * Apple 의 스프링은 물리 3종(질량/강성/감쇠)이 아니라 설계자용
 * 2종으로 표현된다.
 *   damping  - 감쇠비. 1.0 이면 오버슈트 없이 안착. 1.0 미만은 튄다.
 *   response - 목표에 도달하는 체감 속도(초). "지속시간"이 아니다.
 *              스프링은 고정 지속시간을 갖지 않는다.
 */

export type Spring = {
  /** 감쇠비. 1.0 = 임계감쇠(튐 없음). */
  damping: number;
  /** 반응 시간(초). 낮을수록 민첩하다. */
  response: number;
};

/**
 * 기본값. UI 대부분은 튀지 않아야 한다.
 * 탭 전환, 팝오버, 다이얼로그, 필터 적용.
 */
export const SPRING_DEFAULT: Spring = { damping: 1.0, response: 0.35 };

/**
 * 관성이 실린 상호작용 전용. 사용자가 던진 뒤에만 쓴다.
 * 그냥 페이드인한 메뉴가 오버슈트하면 틀린 느낌이 난다.
 */
export const SPRING_MOMENTUM: Spring = { damping: 0.8, response: 0.4 };

/** 시트 / 드로어. Apple 이 실제로 쓰는 값. */
export const SPRING_SHEET: Spring = { damping: 0.8, response: 0.3 };

/**
 * 관성 투사 - 손가락이 "가고 있던 곳"을 계산한다.
 * Designing Fluid Interfaces 샘플 코드의 그 함수다.
 *
 * 교과서의 v^2/(2a) 가 아니라 지수 감쇠형이라는 점이 중요하다.
 * 스크롤 감속과 같은 곡선이어서 손에 익은 느낌이 난다.
 *
 * @param initialVelocity 릴리즈 속도 (px/s)
 * @param decelerationRate 0.998 = 일반 스크롤 감, 0.99 = 더 민첩
 * @returns 현재 위치에서부터의 이동량 (px)
 */
export const project = (initialVelocity: number, decelerationRate = 0.998): number =>
  ((initialVelocity / 1000) * decelerationRate) / (1 - decelerationRate);

/**
 * 러버밴딩 - 경계 밖으로 끌수록 덜 따라온다.
 * 딱 멈추면 "얼었다"로 읽히고, 점진 저항은 "여기가 끝이다"로 읽힌다.
 *
 * overshoot 이 커지면 결과는 dimension 에 점근한다 (절대 그 이상
 * 넘어가지 않는다). 음수 overshoot 에 대해 부호 대칭이다.
 */
export const rubberband = (overshoot: number, dimension: number, constant = 0.55): number =>
  (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));

/**
 * 일부 스프링 API 는 남은 거리로 정규화된 속도를 받는다.
 * (target === current 이면 나눗셈이 터지므로 0 을 돌려준다)
 */
export const relativeVelocity = (
  gestureVelocity: number,
  current: number,
  target: number
): number => {
  const remaining = target - current;
  return remaining === 0 ? 0 : gestureVelocity / remaining;
};

/**
 * (damping, response) -> 단위질량 스프링 계수.
 *   omega = 2 * PI / response
 *   stiffness = omega^2
 *   dampingCoefficient = 2 * damping * omega
 *
 * 자기일관성: dampingCoefficient / (2 * sqrt(stiffness)) === damping.
 * 이 식이 어긋나면 모션이 조용히 "이상한 느낌"만 남기고 아무
 * 에러도 내지 않는다. 그래서 테스트로 못 박아 둔다.
 */
export const springCoefficients = (
  spring: Spring
): { omega: number; stiffness: number; dampingCoefficient: number } => {
  const omega = (2 * Math.PI) / spring.response;
  return {
    omega,
    stiffness: omega * omega,
    dampingCoefficient: 2 * spring.damping * omega,
  };
};

/** 사용자가 모션 감소를 요청했는가. SSR / jsdom 에서는 false. */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/** 제스처가 없는 CSS 트랜지션용 상수. tokens.css 의 값과 같다. */
export const DURATION = {
  /** 눌림 피드백. 포인터 다운에 붙는다. */
  press: 90,
  /** hover, 포커스 링, 틴트. */
  fast: 140,
  /** 팝오버 / 다이얼로그 등장. */
  base: 200,
  /** 시트 슬라이드. */
  slow: 320,
} as const;

export const EASING = {
  standard: "cubic-bezier(0.4, 0, 0.2, 1)",
  /** 들어올 때. */
  out: "cubic-bezier(0.16, 1, 0.3, 1)",
  /** 나갈 때 - out 의 역곡선이라 왕복 경로가 반사된다. */
  in: "cubic-bezier(0.7, 0, 0.84, 0)",
} as const;

export type SpringHandle = {
  /** 목표를 바꾼다. 현재 위치와 속도를 그대로 이어받는다. */
  retarget: (to: number) => void;
  /** 지금 프레임에서 멈춘다. onRest 는 호출하지 않는다. */
  stop: () => void;
};

/**
 * requestAnimationFrame 기반 스프링 적분기.
 *
 * 세 가지를 지킨다.
 *  - 중단 가능: retarget 이 현재 위치/속도에서 이어간다. 목표값에서
 *    다시 시작하지 않으므로 잡아채도 튀지 않는다.
 *  - 속도 인계: velocity 로 릴리즈 속도를 그대로 받는다.
 *  - 모션 감소: 즉시 목표로 점프한다.
 *
 * dt 는 60ms 로 클램프한다. 탭이 백그라운드에 갔다 오면 dt 가
 * 수백 ms 로 튀어서 적분이 발산한다.
 */
export const animateSpring = (opts: {
  from: number;
  to: number;
  velocity?: number;
  spring?: Spring;
  onFrame: (value: number) => void;
  onRest?: () => void;
}): SpringHandle => {
  const { stiffness, dampingCoefficient } = springCoefficients(opts.spring ?? SPRING_DEFAULT);
  let x = opts.from;
  let v = opts.velocity ?? 0;
  let target = opts.to;
  let raf = 0;
  let last = 0;

  if (prefersReducedMotion()) {
    opts.onFrame(target);
    opts.onRest?.();
    return { retarget: (to) => opts.onFrame(to), stop: () => undefined };
  }

  const step = (now: number) => {
    const dt = Math.min((now - last) / 1000, 0.06);
    last = now;

    // 준내재적 오일러. 속도를 먼저 갱신해서 큰 dt 에서도 안정적이다.
    const a = -stiffness * (x - target) - dampingCoefficient * v;
    v += a * dt;
    x += v * dt;

    // 안착 판정: 위치 0.05px 이내 + 속도 0.05px/s 이내.
    if (Math.abs(x - target) < 0.05 && Math.abs(v) < 0.05) {
      x = target;
      opts.onFrame(x);
      opts.onRest?.();
      return;
    }
    opts.onFrame(x);
    raf = requestAnimationFrame(step);
  };

  raf = requestAnimationFrame((now) => {
    last = now;
    raf = requestAnimationFrame(step);
  });

  return {
    retarget: (to) => {
      target = to;
    },
    stop: () => cancelAnimationFrame(raf),
  };
};
